import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Save } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  ReactivationSettingsSchema,
  reactivationSettingsSchema,
  useUpdateReactivationSettings,
} from "@/lib/api/admin.ts";
import TextareaAutosize from "react-textarea-autosize";

interface ReactivationSettingsProps {
  defaultValues: ReactivationSettingsSchema;
}

export function ReactivationSettings({
  defaultValues,
}: ReactivationSettingsProps) {
  const { mutateAsync: updateReactivationSettings, isPending } =
    useUpdateReactivationSettings();

  const form = useForm<ReactivationSettingsSchema>({
    resolver: zodResolver(reactivationSettingsSchema),
    defaultValues,
  });

  const handleSubmit = async (data: ReactivationSettingsSchema) => {
    try {
      await updateReactivationSettings(data);
    } catch (error) {
      console.error("Failed to update reactivation settings:", error);
    }
  };

  return (
    <div className="rounded-xl border border-gray-100 p-4">
      <h2 className="text-lg font-bold">Reactivation Settings</h2>
      <p className="mt-2 text-sm text-gray-500">
        Configure the message sent to teens when they reactivate their account
        and are matched with a new mentor.
      </p>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="mt-6 space-y-6"
        >
          <FormField
            control={form.control}
            name="reactivationMessage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reactivation Message</FormLabel>
                <FormControl>
                  <TextareaAutosize
                    className="w-full rounded-md border border-gray-200 p-2"
                    placeholder="Enter the message sent when a teen reactivates their account..."
                    minRows={5}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <Loader2 className="mr-1 h-5 w-5 animate-spin" />
              ) : (
                <Save className="mr-1 h-5 w-5" />
              )}
              Save
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
